// context/TrackedProgramsProvider.tsx
//
// Shared state for the user-owned tracked-programs feature. Loads the tracked
// map ONCE on mount and shares it across every listing page and the
// MyAcceleratorTracker module (avoiding N fetches). Initial state is an empty
// map, so consumers render fine before/without data.
//
// Mutations are optimistic with rollback on failure.
//
// NOTE: this provider is intentionally NOT mounted anywhere yet — a later phase
// mounts it high enough to wrap BOTH the accelerators/* listing routes AND the
// founders/my-accelerator-tracker route (one shared cache).

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { trackedProgramsService } from '../services/accelerators/founders/api_TrackedPrograms';
import type {
  TrackedProgram,
  TrackedProgramAnnotations,
} from '../services/accelerators/founders/TrackedProgramTypes';

/** Composite key: the SAME "<category>#<id>" identity the backend stores. */
const keyOf = (category: string, id: number): string => `${category}#${id}`;

export interface TrackedProgramsContextValue {
  /** Raw list of tracked programs (map values). */
  trackedPrograms: TrackedProgram[];
  loading: boolean;
  error: string | null;
  isTracked: (category: string, id: number) => boolean;
  track: (category: string, id: number) => Promise<void>;
  untrack: (category: string, id: number) => Promise<void>;
  updateAnnotations: (
    category: string,
    id: number,
    annotations: Partial<TrackedProgramAnnotations>
  ) => Promise<void>;
  /** Force a reload from the backend. */
  refresh: () => Promise<void>;
}

const TrackedProgramsContext = createContext<
  TrackedProgramsContextValue | undefined
>(undefined);

export const TrackedProgramsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Keyed map so isTracked / mutations are O(1); initial state = empty map.
  const [map, setMap] = useState<Record<string, TrackedProgram>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await trackedProgramsService.getTrackedPrograms();
      const next: Record<string, TrackedProgram> = {};
      (res.data ?? []).forEach((tp) => {
        next[keyOf(tp.category, tp.id)] = tp;
      });
      setMap(next);
      setError(null);
    } catch (e: any) {
      console.error('Failed to load tracked programs:', e);
      setError(e?.message ?? 'Failed to load tracked programs');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load ONCE on mount.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const isTracked = useCallback(
    (category: string, id: number): boolean => !!map[keyOf(category, id)],
    [map]
  );

  const track = useCallback(
    async (category: string, id: number): Promise<void> => {
      const k = keyOf(category, id);
      if (map[k]) return; // idempotent: already tracked

      const prev = map;
      const optimistic: TrackedProgram = {
        category,
        id,
        addedAt: new Date().toISOString(),
        annotations: { status: 'saved' },
      };
      setMap((m) => ({ ...m, [k]: optimistic }));

      try {
        await trackedProgramsService.trackProgram(category, id);
      } catch (e: any) {
        console.error('Failed to track program:', e);
        setMap(prev); // rollback
        setError(e?.message ?? 'Failed to track program');
        throw e;
      }
    },
    [map]
  );

  const untrack = useCallback(
    async (category: string, id: number): Promise<void> => {
      const k = keyOf(category, id);
      if (!map[k]) return;

      const prev = map;
      setMap((m) => {
        const next = { ...m };
        delete next[k];
        return next;
      });

      try {
        await trackedProgramsService.untrackProgram(category, id);
      } catch (e: any) {
        console.error('Failed to untrack program:', e);
        setMap(prev); // rollback
        setError(e?.message ?? 'Failed to untrack program');
        throw e;
      }
    },
    [map]
  );

  const updateAnnotations = useCallback(
    async (
      category: string,
      id: number,
      annotations: Partial<TrackedProgramAnnotations>
    ): Promise<void> => {
      const k = keyOf(category, id);
      const existing = map[k];
      if (!existing) return;

      const prev = map;
      const mergedAnnotations: TrackedProgramAnnotations = {
        ...existing.annotations,
        ...annotations,
        updatedAt: new Date().toISOString(),
      };
      setMap((m) => ({
        ...m,
        [k]: { ...existing, annotations: mergedAnnotations },
      }));

      try {
        await trackedProgramsService.updateTrackedProgram(
          category,
          id,
          annotations
        );
      } catch (e: any) {
        console.error('Failed to update annotations:', e);
        setMap(prev); // rollback
        setError(e?.message ?? 'Failed to update annotations');
        throw e;
      }
    },
    [map]
  );

  const value: TrackedProgramsContextValue = {
    trackedPrograms: Object.values(map),
    loading,
    error,
    isTracked,
    track,
    untrack,
    updateAnnotations,
    refresh,
  };

  return (
    <TrackedProgramsContext.Provider value={value}>
      {children}
    </TrackedProgramsContext.Provider>
  );
};

/** Hook to consume the tracked-programs shared state. */
export const useTrackedPrograms = (): TrackedProgramsContextValue => {
  const context = useContext(TrackedProgramsContext);
  if (context === undefined) {
    throw new Error(
      'useTrackedPrograms must be used within a TrackedProgramsProvider'
    );
  }
  return context;
};

export default TrackedProgramsProvider;
